import PropTypes from 'prop-types';
import { HiOutlineMail } from 'react-icons/hi';

import useContactRoles from '#globalHooks/useContactRoles';

import ContactPhone from '../../ContactPhone/ContactPhone';

/**
 * Read-only "Firmenkontakt" row derived from the company's general email address.
 *
 * This is NOT a real contact — there is no contact record behind it, so it has no
 * checkbox and cannot be selected. It mirrors the visual style of a contact row so the
 * company email keeps looking like the familiar "Firmenkontakt" entry. To target the
 * company itself, the user selects the company via its table-row checkbox (lead selection).
 */
const CompanyContactRow = ({ companyEmail }) => (
	<div className="w-full flex items-center gap-3 px-3 py-2 rounded-lg border border-transparent">
		<div className="flex-1 min-w-0 flex items-center gap-4">
			<span className="text-sm font-medium text-text truncate w-48">Firmenkontakt</span>
			<span className="text-xs text-bgBase-800 truncate w-40">–</span>
			<span className="text-xs text-primary-700 truncate flex items-center gap-1">
				<HiOutlineMail className="w-3 h-3 flex-shrink-0" />
				{companyEmail}
			</span>
		</div>
	</div>
);

CompanyContactRow.propTypes = {
	companyEmail: PropTypes.string.isRequired,
};

/**
 * ContactExpandRow Component - Expandable row showing contacts for a lead.
 *
 * Renders the real (selectable) contacts plus, when the company has a general email
 * address, a read-only "Firmenkontakt" entry derived from it (see {@link CompanyContactRow}).
 */
const ContactExpandRow = ({ contacts, companyEmail, selectedContacts, onContactToggle, colSpan, lockedPhoneCount }) => {
	const { roleLabel } = useContactRoles();
	const hasCompanyEmail = Boolean(companyEmail);
	const isEmpty = contacts.length === 0 && !hasCompanyEmail;

	return (
		<tr className="border-b border-gray-200">
			<td colSpan={colSpan} className="p-0">
				<div className="bg-gray-50/80 border-t border-gray-100">
					<div className="px-6 py-3">
						{isEmpty ? (
							<p className="text-sm text-bgBase-800 italic py-2">Keine Kontakte vorhanden</p>
						) : (
							<div className="space-y-1">
								{hasCompanyEmail && <CompanyContactRow companyEmail={companyEmail} />}
								{contacts.map((contact) => {
									const isSelected = selectedContacts.includes(contact.id);
									return (
										<button
											key={contact.id}
											onClick={() => onContactToggle(contact.id)}
											className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left ${
												isSelected
													? 'bg-purple-50 border border-purple-200'
													: 'hover:bg-white border border-transparent'
											}`}
										>
											<div
												className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
													isSelected ? 'border-purple-600 bg-purple-600' : 'border-gray-300'
												}`}
											>
												{isSelected && (
													<svg
														className="w-2.5 h-2.5 text-white"
														fill="currentColor"
														viewBox="0 0 20 20"
													>
														<path
															fillRule="evenodd"
															d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
															clipRule="evenodd"
														/>
													</svg>
												)}
											</div>
											<div className="flex-1 min-w-0 flex items-center gap-4">
												<span className="text-sm font-medium text-text truncate w-48">
													{contact.fullname || 'Unbekannt'}
												</span>
												<span className="text-xs text-bgBase-800 truncate w-40">
													{roleLabel(contact.companyRole) || '–'}
												</span>
												{contact.emailAddress ? (
													<span className="text-xs text-primary-700 truncate flex items-center gap-1">
														<HiOutlineMail className="w-3 h-3 flex-shrink-0" />
														{contact.emailAddress}
													</span>
												) : (
													<span className="text-xs text-bgBase-800 truncate italic">
														Keine E-Mail
													</span>
												)}
												<ContactPhone
													contact={contact}
													lockedCount={lockedPhoneCount}
													className="w-64"
												/>
											</div>
										</button>
									);
								})}
							</div>
						)}
					</div>
				</div>
			</td>
		</tr>
	);
};

ContactExpandRow.propTypes = {
	contacts: PropTypes.array.isRequired,
	companyEmail: PropTypes.string,
	selectedContacts: PropTypes.array.isRequired,
	onContactToggle: PropTypes.func.isRequired,
	colSpan: PropTypes.number.isRequired,
	lockedPhoneCount: PropTypes.number,
};

export default ContactExpandRow;
